// @ai-generated 2026-09-03T14:20:00+00:00 by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import { AGENTIC_ORCHESTRATOR_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { workflowRunColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useHasSystemAccess } from '@Contexts/session/useHasSystemAccess';
import { useWorkflowDefinitionStory } from '@Hooks/AgenticOrchestrator/useWorkflowStory';
import { useTransparentRef } from '@Hooks/useTransparentRef';
import { type ApiWorkflowDefinition, type ApiWorkflowRun } from '@Model';
import { FrameEditWorkflowRun } from '@Modules/AgenticOrchestrator';
import { WorkflowStepList } from '../WorkflowStepList';
import { yaml as yamlLang } from '@codemirror/lang-yaml';
import {
    Card,
    DateInputBase,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TableWithStoredSettings,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useOnTheFlyModal,
    useTranslate,
    type ListRequest,
    unwrapOr,
} from '@synqo/frontend-component-library';
import CodeMirror from '@uiw/react-codemirror';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useAgenticOrchestratorRepository } from '@Hooks/useRepository';
import { WorkflowDefinitionsRepository } from '@Repositories/AgenticOrchestrator';
import { emptyPage } from '@Hooks/useListApi';

/**
 * Detail view for a WorkflowDefinition. Authoring is reserved for the seeding
 * client.
 */
export function FrameEditWorkflowDefinition(props: IFrameEditShellProps<ApiWorkflowDefinition>) {
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const hasPermission = useHasPermission();
    const hasSystemAccess = useHasSystemAccess();
    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiWorkflowDefinition>
            apiResourcePath={`${AGENTIC_ORCHESTRATOR_API}/workflow_definitions`}
            renderer={FrameEditWorkflowDefinitionBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={
                props.canEdit !== false &&
                hasSystemAccess &&
                hasPermission(Permission.AgenticOrchestratorWorkflowDefinitionsUpdate)
            }
            canCreate={
                props.canCreate !== false &&
                hasSystemAccess &&
                hasPermission(Permission.AgenticOrchestratorWorkflowDefinitionsCreate)
            }
            canDelete={
                props.canDelete !== false &&
                hasSystemAccess &&
                hasPermission(Permission.AgenticOrchestratorWorkflowDefinitionsDelete)
            }
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

const lockedOnChange = () => false;

function WorkflowDefinitionStory({ workflowDefinitionId }: { workflowDefinitionId: string }) {
    const { view, error } = useWorkflowDefinitionStory(workflowDefinitionId);

    if (error || !view) {
        return null;
    }

    return <WorkflowStepList steps={view.steps} mode={view.mode} />;
}

function FrameEditWorkflowDefinitionBody(props: IFrameBodyProps<ApiWorkflowDefinition>) {
    const i18n = useTranslate('');
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();

    const isViewMode = props.mode === EditFrameMode.View;

    const binder = useGuiBinder<ApiWorkflowDefinition>({
        ...props,
        canEdit: !isViewMode,
    });

    const yamlExtensions = useMemo(() => [yamlLang()], []);
    const { onChange: onYamlChange, onConfirm: onYamlConfirm, locked: yamlLocked } = binder.bindToProp('yaml');

    const yaml = useTransparentRef(props.data.yaml, isViewMode);

    const handleYamlChange = useCallback(
        (value: string) => {
            if (!onYamlChange(value, true)) {
                return;
            }

            yaml.current = value;
            return true;
        },
        [onYamlChange, yaml],
    );

    const handleYamlConfirm = useCallback(() => onYamlConfirm?.(yaml.current, true), [onYamlConfirm, yaml]);

    // Runs are queried through the definition-scoped route so the WorkflowDefinitionId filter
    // is applied server-side (mirrors the run-scoped steps route).
    const workflowDefinitions = useAgenticOrchestratorRepository(WorkflowDefinitionsRepository);
    const loadRelatedRuns = useCallback(
        async (listRequest: ListRequest) =>
            unwrapOr(await workflowDefinitions.getRuns(props.data.id, listRequest, ''), emptyPage<ApiWorkflowRun>()),
        [workflowDefinitions, props.data.id],
    );

    const openRunModal = useCallback(
        (run: ApiWorkflowRun) => {
            let modalId = '';
            modalId = openOnTheFlyModal({
                child: (
                    <FrameEditWorkflowRun primaryKey={run.id} embedded onClose={() => closeOnTheFlyModals(modalId)} />
                ),
            });
            return true;
        },
        [openOnTheFlyModal, closeOnTheFlyModals],
    );

    const runsSorting = useMemo(() => ({ columnName: 'created_at' as const, direction: 'desc' as const }), []);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Workflow-Definition', props.mode),
                resourceLabel: 'Workflow-Definition',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="HeadData" label="Definition" collapsable={false} border="withoutBorder">
                <Grid columns={12}>
                    <GridColumns spans={[4, 4, 4]}>
                        <TextInput {...binder.bindToProp<string>('definition_key')} label={i18n('Key')} />
                        <TextInput {...binder.bindToProp<string>('label')} label={i18n('Bezeichnung')} />
                        <TextInput {...binder.bindToProp<string>('shorthand')} label={i18n('Suchcode')} />
                    </GridColumns>
                    {isViewMode && (
                        <GridColumns spans={[6, 6]}>
                            <DateInputBase
                                name="created_at"
                                label={i18n('Angelegt')}
                                type="datetime"
                                value={props.data.created_at}
                                onChange={lockedOnChange}
                                locked
                            />
                            <DateInputBase
                                name="modified_at"
                                label={i18n('Geändert')}
                                type="datetime"
                                value={props.data.modified_at}
                                onChange={lockedOnChange}
                                locked
                            />
                        </GridColumns>
                    )}
                    <GridColumn span={12}>
                        <TextInput
                            {...binder.bindToProp<string>('remarks')}
                            inputSize={3}
                            label={i18n('Beschreibung')}
                        />
                    </GridColumn>
                    {!isViewMode && (
                        <GridColumn span={12}>
                            <TextInput
                                {...binder.bindToProp<string>('llm_description')}
                                inputSize={3}
                                label={i18n('Beschreibung für die KI')}
                            />
                        </GridColumn>
                    )}
                </Grid>
            </Card>
            {isViewMode ? (
                <Card name="Story" label="Ablauf">
                    <WorkflowDefinitionStory workflowDefinitionId={props.data.id} />
                </Card>
            ) : (
                <Card name="Yaml" label="YAML">
                    <CodeMirror
                        key={props.mode}
                        value={yaml.current}
                        height="600px"
                        extensions={yamlExtensions}
                        onChange={handleYamlChange}
                        onBlur={handleYamlConfirm}
                        editable={!yamlLocked}
                    />
                </Card>
            )}

            {isViewMode && (
                <Card name="Runs" label="Läufe">
                    <TableWithStoredSettings<ApiWorkflowRun>
                        name="WorkflowDefinitionRelatedRuns"
                        columns={workflowRunColumns}
                        initialSorting={runsSorting}
                        loader={loadRelatedRuns}
                        canCreate={false}
                        onSelect={openRunModal}
                    />
                </Card>
            )}
        </EditFrameShell>
    );
}
